import { Opportunity } from "@entities";
import { MoreVert } from "@mui/icons-material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import Battery1BarRoundedIcon from "@mui/icons-material/Battery1BarRounded";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import BorderColorRoundedIcon from "@mui/icons-material/BorderColorRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DifferenceRoundedIcon from "@mui/icons-material/DifferenceRounded";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";
import InfoIcon from "@mui/icons-material/Info";
import InsertLinkRoundedIcon from "@mui/icons-material/InsertLinkRounded";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import {
  createTheme,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  ThemeProvider,
} from "@mui/material";
import { opportunityRepo } from "@repositories";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useState, type FC } from "react";
import { isMobile } from "react-device-detect";
import { COLORS } from "../../../scss/Variables";
import { useAlert } from "../../contexts/AlertProvider";
import { useOrganization } from "../../contexts/OrganizationProvider";
import {
  ADMIN_GROUP_CLOSE_ALL,
  ADMIN_GROUP_CLOSE_ALL_HINT,
  ADMIN_GROUP_CLOSED_ALL,
  ADMIN_GROUP_INSTANCES_TAB,
  ADMIN_GROUP_REOPENED_ALL,
  ADMIN_GROUP_REOPEN_ALL,
} from "@shared/helpers/opportunity-group-admin-copy";
import { allowsGroupVolunteer } from "@shared/helpers/opportunity-volunteer-mode";
import useQuestion from "../../dialog-components/QuestionDialog";
import { TableToolbarTabs } from "../../pages/tables/table-tabs/TableToolbarTabs";
import "./EventSidebar.scss";

const MOBILE_FILTERED_GENERAL_ACTIONS = [
  "צפייה בדיווחים",
  "העתקת קישור",
];
const MOBILE_THEME = createTheme({
  palette: {
    primary: {
      main: COLORS["main-color"],
    },
  },
});

import InsightsIcon from "@mui/icons-material/Insights";
import ThumbsUpDownIcon from "@mui/icons-material/ThumbsUpDown";
import AddCommentIcon from "@mui/icons-material/AddComment";

interface EventSideBar {
  eventDetails: Opportunity | undefined;
  setSelectedList: React.Dispatch<React.SetStateAction<string>>;
  selectedList: string;
  refetchEvent?: VoidFunction;
  onOpenPartnerOrganization?: () => void;
  tourAnchors?: boolean;
  previewMode?: boolean;
}

export const EventSideBar: FC<EventSideBar> = ({
  eventDetails,
  selectedList,
  setSelectedList,
  refetchEvent,
  onOpenPartnerOrganization,
  tourAnchors = false,
  previewMode = false,
}) => {
  const { setAlertText, setTime, setSeverity } = useAlert();
  const question = useQuestion();
  const style = {
    color: "gray",
    fontSize: "1.3rem",
  };
  const queryClient = useQueryClient();
  const [extraActionsAnchorEL, setExtraActionsAnchorEL] =
    useState<HTMLElement | null>(null);
  const [hovered, setHovered] = useState<boolean>(false);

  const { currOrganization } = useOrganization();
  const isPremium = currOrganization?.organizationType === "PREMIUM";

  const { data: groupChildCount = 0 } = useQuery({
    queryKey: ["group-parent-child-count", eventDetails?.id],
    queryFn: () =>
      opportunityRepo.count({
        parentOpportunityId: eventDetails!.id!,
        isDeleted: false,
      }),
    enabled: !previewMode && Boolean(eventDetails?.id),
  });
  const isGroupWithInstances =
    !!eventDetails?.isOpportunityGroupParent || groupChildCount > 0;

  const invalidateEventQueries = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["events"],
    });
  };

  const handleMouseEnter = () => {
    setHovered(true);
  };

  const handleMouseLeave = () => {
    setHovered(false); // Reset immediately on mouse leave
  };

  const getShareLink = () => {
    navigator.clipboard.writeText(
      `https://www.we-me.app/opportunity/${eventDetails?.id}`,
    );
    setAlertText("הקישור הועתק ללוח");
    setTime(3500);
    setSeverity("success");
  };

  const generalActions = (
    (new Date(eventDetails?.startDate || "") >= new Date() ||
      (!eventDetails?.hasStartTime &&
        new Date(eventDetails?.endDate || "") >= new Date()) ||
      !eventDetails?.hasTime) &&
      !eventDetails?.isClosed
      ? [
        {
          title: "פרטי המשימה",
          icon: <InfoIcon style={style} />,
          onclick: () => setSelectedList(""),
          name: "",
        },
        {
          title: "תיעוד תקשורת",
          icon: <AddCommentIcon style={style} />,
          onclick: () => setSelectedList("communication"),
          name: "communication",
        },
        onOpenPartnerOrganization
          ? {
            title: "ארגון שותף",
            icon: <CorporateFareIcon style={style} />,
            onclick: onOpenPartnerOrganization,
            name: "partner-organization",
          }
          : undefined,
        !isGroupWithInstances || tourAnchors
          ? {
            title: "הוספת משתתפים",
            icon: <GroupAddRoundedIcon style={style} />,
            onclick: () => {
              setSelectedList("add-participants");
            },
            name: "add-participants",
          }
          : undefined,
        !isGroupWithInstances || tourAnchors
          ? {
            title: "מתנדבים רלוונטיים",
            icon: <PersonSearchIcon style={style} />,
            onclick: () => {
              setSelectedList("relevant-volunteers");
            },
            name: "relevant-volunteers",
          }
          : undefined,
        {
          title: "צפייה בדיווחים",
          icon: <ThumbsUpDownIcon style={style} />,
          onclick: () => {
            setSelectedList("reports");
          },
          name: "reports",
        },
        {
          title: "ניתוח הרשמות",
          icon: <InsightsIcon style={style} />,
          onclick: () => {
            setSelectedList("registration-analytics");
          },
          name: "registration-analytics",
        },
        {
          title: "העתקת קישור",
          icon: <InsertLinkRoundedIcon style={style} />,
          onclick: getShareLink,
          name: "copy-link",
        },
        isPremium
          ? {
            title: "הוספת מטלה",
            icon: <AssignmentIcon style={style} />,
            onclick: () => {
              setSelectedList("add-mission");
            },
            name: "add-mission",
          }
          : undefined,
      ]
      : [
        {
          title: "פרטי המשימה",
          icon: <InfoIcon style={style} />,
          onclick: () => setSelectedList(""),
          name: "",
        },
        {
          title: "תיעוד תקשורת",
          icon: <AddCommentIcon style={style} />,
          onclick: () => setSelectedList("communication"),
          name: "communication",
        },
        onOpenPartnerOrganization
          ? {
            title: "ארגון שותף",
            icon: <CorporateFareIcon style={style} />,
            onclick: onOpenPartnerOrganization,
            name: "partner-organization",
          }
          : undefined,
        {
          title: "צפייה בדיווחים",
          icon: <ThumbsUpDownIcon style={style} />,
          onclick: () => {
            setSelectedList("reports");
          },
          name: "reports",
        },
        {
          title: "ניתוח הרשמות",
          icon: <InsightsIcon style={style} />,
          onclick: () => {
            setSelectedList("registration-analytics");
          },
          name: "registration-analytics",
        },
      ]
  ).filter(Boolean) as {
    title: string;
    icon: JSX.Element;
    onclick: () => void;
    name: string;
  }[];

  if (isGroupWithInstances) {
    generalActions.splice(1, 0, {
      title: ADMIN_GROUP_INSTANCES_TAB,
      icon: <CalendarMonthIcon style={style} />,
      onclick: () => setSelectedList("group-slots"),
      name: "group-slots",
    });
  }

  if (
    allowsGroupVolunteer(eventDetails?.volunteerMode) &&
    (new Date(eventDetails?.startDate || "") >= new Date() ||
      (!eventDetails?.hasStartTime &&
        new Date(eventDetails?.endDate || "") >= new Date()) ||
      !eventDetails?.hasTime) &&
    !eventDetails?.isClosed
  ) {
    generalActions.push({
      title: "בקשות הצטרפות",
      icon: (
        <Stack position="relative">
          <FactCheckIcon style={style} />{" "}
          {(eventDetails?.interestedCount || 0) > 0 && (
            <div
              style={{
                backgroundColor: "#00b4d8",
                borderRadius: "20px",
                padding: "0.2rem",
                right: -5,
                fontSize: "0.7rem",
                top: -7,
                color: "white",
                position: "absolute",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {eventDetails?.interestedCount}
            </div>
          )}
        </Stack>
      ),
      onclick: () => {
        setSelectedList("requests");
      },
      name: "requests",
    });
  }

  const extraActions = [
    {
      title: "עריכה",
      icon: <BorderColorRoundedIcon style={style} />,
      onclick: () => {
        setSelectedList("edit");
      },
      name: "edit",
    },
    {
      title: "שכפול",
      icon: <DifferenceRoundedIcon style={style} />,
      onclick: () => {
        setSelectedList("duplicate");
      },
      name: "duplicate",
    },
    {
      title: "מחיקה",
      icon: <DeleteRoundedIcon style={style} />,
      onclick: () => {
        setSelectedList("delete");
      },
      name: "delete",
    },
  ];
  const isGroupParent = isGroupWithInstances;
  const groupParentId = eventDetails?.id || "";

  if (eventDetails?.isDeleted) {
    extraActions.unshift({
      title: "שחזור מארכיון",
      icon: <UnarchiveIcon style={style} />,
      onclick: () => {
        setSelectedList("restore");
      },
      name: "restore",
    });
  }

  if (!eventDetails?.isClosed) {
    extraActions.unshift({
      title: "סימון כטופלה",
      icon: <LockIcon style={style} />,
      onclick: () => {
        question(
          isGroupParent
            ? ADMIN_GROUP_CLOSE_ALL
            : "האם ברצונך לסגור את המשימה?",
          isGroupParent
            ? ADMIN_GROUP_CLOSE_ALL_HINT
            : "",
        ).then(async (yes) => {
          if (yes) {
            if (isGroupParent) {
              await opportunityRepo.updateMany({
                where: { parentOpportunityId: groupParentId, isDeleted: false },
                set: { isClosed: true },
              });
            }
            await opportunityRepo.update(groupParentId, { isClosed: true });
            if (isGroupParent) {
              await Opportunity.syncGroupParentFromChildren(groupParentId);
            }
            invalidateEventQueries();
            refetchEvent?.();
            setAlertText(
              isGroupParent ? ADMIN_GROUP_CLOSED_ALL : "המשימה סגורה",
            );
            setSeverity("success");
          }
        });
      },
      name: "close",
    });
  }

  if (eventDetails?.isClosed) {
    extraActions.unshift({
      title: "פתיחת משימה",
      icon: <LockOpenIcon style={style} />,
      onclick: () => {
        question(
          isGroupParent
            ? ADMIN_GROUP_REOPEN_ALL
            : "האם ברצונך לפתוח את המשימה?",
        ).then(async (yes) => {
          if (yes) {
            if (isGroupParent) {
              await opportunityRepo.updateMany({
                where: { parentOpportunityId: groupParentId, isDeleted: false },
                set: { isClosed: false },
              });
            }
            await opportunityRepo.update(groupParentId, { isClosed: false });
            if (isGroupParent) {
              await Opportunity.syncGroupParentFromChildren(groupParentId);
            }
            invalidateEventQueries();
            refetchEvent?.();
            setAlertText(
              isGroupParent
                ? ADMIN_GROUP_REOPENED_ALL
                : "המשימה פתוחה להרשמה",
            );
            setSeverity("success");
          }
        });
      },
      name: "close",
    });
  }

  if (
    !isGroupParent &&
    !eventDetails?.isFull &&
    (eventDetails?.amount || 0) > (eventDetails?.participantsCount || 0)
  ) {
    extraActions.unshift({
      title: "מילוי משימה",
      icon: <BatteryFullIcon style={style} />,
      onclick: () => {
        question("האם ברצונך למלא את המשימה?").then(async (yes) => {
          if (yes) {
            await opportunityRepo.update(eventDetails?.id || "", {
              isFull: true,
            });
            invalidateEventQueries();
            refetchEvent?.();
            setAlertText("המשימה סגורה להרשמה");
            setSeverity("success");
          }
        });
      },
      name: "close",
    });
  }

  if (!isGroupParent && eventDetails?.isFull) {
    extraActions.unshift({
      title: "ביטול מילוי משימה",
      icon: <Battery1BarRoundedIcon style={style} />,
      onclick: () => {
        question("האם ברצונך לבטל את מילוי המשימה?").then(async (yes) => {
          if (yes) {
            await opportunityRepo.update(eventDetails?.id || "", {
              isFull: false,
            });
            invalidateEventQueries();
            refetchEvent?.();
            setAlertText("המשימה פתוחה להרשמה");
            setSeverity("success");
          }
        });
      },
      name: "full",
    });
  }

  if (isMobile) {
    extraActions.unshift({
      title: "העתקת קישור",
      icon: <InsertLinkRoundedIcon style={style} />,
      onclick: getShareLink,
      name: "copy-link",
    });
  }

  function handleClose() {
    setExtraActionsAnchorEL(null);
  }

  const mobileOptions = generalActions
    .filter(({ title }) => !MOBILE_FILTERED_GENERAL_ACTIONS.includes(title))
    .map(({ title, name }) => ({ content: title, value: name }));
  const isMobileSelectedList = mobileOptions.some(
    ({ value }) => value === selectedList,
  );

  return (
    <>
      {isMobile ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="96%"
          alignSelf="center"
        >
          <TableToolbarTabs
            value={isMobileSelectedList ? selectedList : "else"}
            onChange={setSelectedList}
            options={mobileOptions}
          />
          <ThemeProvider theme={MOBILE_THEME}>
            <IconButton
              color="primary"
              onClick={(e) => setExtraActionsAnchorEL(e.currentTarget)}
              sx={{ marginTop: "1vh" }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu
              open={!!extraActionsAnchorEL}
              anchorEl={extraActionsAnchorEL}
              onClose={handleClose}
            >
              <MenuList>
                {extraActions.map((action, index) => {
                  return (
                    <MenuItem
                      color="primary"
                      key={action.name + index}
                      onClick={() => {
                        action.onclick();
                        handleClose();
                      }}
                    >
                      <ListItemIcon>{action.icon}</ListItemIcon>
                      <ListItemText>{action.title}</ListItemText>
                    </MenuItem>
                  );
                })}
              </MenuList>
            </Menu>
          </ThemeProvider>
        </Stack>
      ) : (
        <div
          className={clsx(
            "event-sidebar-container",
            (selectedList === "edit" || selectedList === "duplicate") &&
              "small",
            hovered && "hovered",
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="actions">
            <p className="par">פעולות כלליות</p>
            {generalActions.map((action, index) => {
              return (
                <button
                  key={action.name + index}
                  data-tour={
                    action.name === "add-participants"
                      ? "cc-add-participants"
                      : action.name === "relevant-volunteers"
                        ? "cc-relevant-nav"
                        : undefined
                  }
                  className={clsx(
                    "action-btn",
                    action.name === selectedList && "selected",
                  )}
                  onClick={action.onclick}
                >
                  {action.icon}
                  <span className="action-title">{action.title}</span>
                </button>
              );
            })}
          </div>
          <div className="actions">
            <p className="par">פעולות נוספות</p>
            {extraActions.map((action, index) => {
              return (
                <button
                  key={action.name + index}
                  className={clsx(
                    "action-btn",
                    action.name === selectedList && "selected",
                  )}
                  onClick={action.onclick}
                >
                  {action.icon}
                  <span className="action-title">{action.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};
